import { Loadable } from '@/components/loaders';
import { useCompany } from '@/features/company/hooks';
import { isCompanyAccounting, isCompanyAdmin } from '@/features/userInfo/hooks/useUserInfo';
import { PageLayout } from '@/global/components';
import { BreadcrumbItem, Breadcrumbs } from '@/lib/Breadcrumbs';
import { Button } from '@/lib/Buttons';
import { Input } from '@/lib/Input';
import { CompanyAdministrativeData } from '@/models/company';
import { useLazyGetCompanyAdministativeDataQuery, useUpdateCompanyAdministrativeDataMutation } from '@/services/endpoints/companies';
import { Form, Formik, FormikProps } from 'formik';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import RouteConstants from '../../router/RouteConstants';

export const CompanySettings: FC = () => {
  const { t } = useTranslation();
  const company = useCompany();

  const formRef = useRef<FormikProps<CompanyAdministrativeData>>(null);

  const validationSchema = Yup.object({
    legalEntity: Yup.string().required(t('settings:company.errors.legalEntityRequired') as string),
    licenseNumber: Yup.string().required(t('settings:company.errors.licenseNumberRequired') as string),
    vatNumber: Yup.string().required(t('settings:company.errors.vatNumberRequired') as string),
    email: Yup.string()
      .email(t('settings:company.errors.emailValid') as string)
      .required(t('settings:company.errors.emailRequired') as string),
    legalAddress1: Yup.string().required(t('settings:company.errors.legalAddress1Required') as string),
    legalAddress2: Yup.string(),
    city: Yup.string(),
    state: Yup.string(),
    zip: Yup.string(),
    country: Yup.string().required(t('settings:company.errors.countryRequired') as string),
  });

  const initialSettings: CompanyAdministrativeData = {
    legalEntity: '',
    licenseNumber: '',
    email: '',
    vatNumber: '',
    legalAddress1: '',
    legalAddress2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    createdOn: '',
    updatedOn: '',
  };

  const canEdit = isCompanyAdmin() || isCompanyAccounting();

  const [getAdministativeData, { data: loadedSettings, isLoading: isSettingsLoading, isError: isLoadError }] = useLazyGetCompanyAdministativeDataQuery();

  useEffect(() => {
    if (canEdit) {
      getAdministativeData({
        companyId: company.id,
      });
    }
  }, [canEdit]);

  const [updateSettings, { isLoading: isUpdateLoading }] = useUpdateCompanyAdministrativeDataMutation();

  useEffect(() => {
    if (!isSettingsLoading && !isLoadError && loadedSettings) {
      formRef.current?.setValues(
        {
          legalEntity: loadedSettings.legalEntity || '',
          licenseNumber: loadedSettings.licenseNumber || '',
          email: loadedSettings.email || '',
          vatNumber: loadedSettings.vatNumber || '',
          legalAddress1: loadedSettings.legalAddress1 || '',
          legalAddress2: loadedSettings.legalAddress2 || '',
          city: loadedSettings.city || '',
          state: loadedSettings.state || '',
          zip: loadedSettings.zip || '',
          country: loadedSettings.country || '',
          createdOn: loadedSettings.createdOn,
          updatedOn: loadedSettings.updatedOn,
        },
        false
      );
    }
  }, [isSettingsLoading, isLoadError, loadedSettings]);

  const handleSaveButtonClicked = async () => {
    await formRef.current?.submitForm();
    const values = formRef.current?.values;

    if (formRef.current?.isValid && values) {
      updateSettings({ companyId: company.id, body: values });
    }
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      label: 'settings:breadcrumb',
      path: RouteConstants.settings,
    },
    {
      label: 'settings:tabs.company',
    },
  ];

  return (
    <PageLayout>
      <Breadcrumbs items={breadcrumbs} />

      <div className='mb-6 flex items-start justify-between'>
        <div className='grid gap-1'>
          <h2 className='text-2xl font-semibold text-primary-900'>{company.name}</h2>
          <p className='text-secondary-700'>{t('settings:subtitle_company')}</p>
        </div>
      </div>

      <Loadable isLoading={isSettingsLoading && canEdit}>
        <Formik initialValues={initialSettings} validationSchema={validationSchema} innerRef={formRef} onSubmit={() => {}}>
          {({ values, handleChange, errors }) => (
            <Form>
              <div className='grid gap-6 rounded-xl border border-primary-border bg-white-100 p-6 shadow-xs-skeumorphic'>
              <div>
                <div className='flex justify-between'>
                  <p className='mb-8 mt-6 text-sm text-secondary-700'>{t('company:administrative_info.administrative_info')}</p>
                  {canEdit ? (
                    <Button
                      intent='primary'
                      size='sm'
                      onClick={handleSaveButtonClicked}
                      isLoading={isUpdateLoading}
                      text={t('workspaces:edit.save') as string}
                    />
                  ) : null}
                </div>

                <div className='grid grid-cols-1 gap-6'>
                  <div className='grid gap-1 sm:grid-cols-12 sm:items-center'>
                    <label className='text-sm font-medium text-gray-700 sm:col-span-1'>{t('settings:company.company')}</label>
                    <div className='sm:col-span-11'>
                      <Input value={company.name} readOnly disabled />
                    </div>
                  </div>

                  <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
                    <div className='grid gap-1 sm:grid-cols-12 sm:items-center'>
                      <label className='text-sm font-medium text-gray-700 sm:col-span-2'>{t('settings:company.entity')}</label>
                      <div className='sm:col-span-10'>
                        <Input
                          value={values.legalEntity}
                          onChange={(e) => handleChange('legalEntity')(e.target.value)}
                          disabled={isSettingsLoading || isUpdateLoading || !canEdit}
                          errorMsg={errors.legalEntity as string}
                        />
                      </div>
                    </div>

                    <div className='grid gap-1 sm:grid-cols-12 sm:items-center'>
                      <label className='text-sm font-medium text-gray-700 sm:col-span-2'>{t('settings:company.license')}</label>
                      <div className='sm:col-span-10'>
                        <Input
                          value={values.licenseNumber}
                          onChange={(e) => handleChange('licenseNumber')(e.target.value)}
                          disabled
                          errorMsg={errors.licenseNumber as string}
                        />
                      </div>
                    </div>

                    <div className='grid gap-1 sm:grid-cols-12 sm:items-center'>
                      <label className='text-sm font-medium text-gray-700 sm:col-span-2'>{t('settings:company.vat')}</label>
                      <div className='sm:col-span-10'>
                        <Input
                          value={values.vatNumber}
                          onChange={(e) => handleChange('vatNumber')(e.target.value)}
                          disabled={!canEdit}
                          errorMsg={errors.vatNumber as string}
                        />
                      </div>
                    </div>

                    <div className='grid gap-1 sm:grid-cols-12 sm:items-center'>
                      <label className='text-sm font-medium text-gray-700 sm:col-span-2'>{t('settings:company.email')}</label>
                      <div className='sm:col-span-10'>
                        <Input
                          type='email'
                          value={values.email}
                          onChange={(e) => handleChange('email')(e.target.value)}
                          disabled={!canEdit}
                          errorMsg={errors.email as string}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='border-t pt-4'>
                <label className='mb-4 block text-sm font-semibold text-secondary-700'>{t('settings:company.address.title')}</label>
                <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
                  <div className='grid gap-1 sm:grid-cols-12 sm:items-center'>
                    <label className='text-sm font-medium text-gray-700 sm:col-span-2'>{t('settings:company.address.line1')}</label>
                    <div className='sm:col-span-10'>
                      <Input
                        value={values.legalAddress1}
                        onChange={(e) => handleChange('legalAddress1')(e.target.value)}
                        disabled={!canEdit}
                        errorMsg={errors.legalAddress1 as string}
                      />
                    </div>
                  </div>
                  <div className='grid gap-1 sm:grid-cols-12 sm:items-center'>
                    <label className='text-sm font-medium text-gray-700 sm:col-span-2'>{t('settings:company.address.line2')}</label>
                    <div className='sm:col-span-10'>
                      <Input
                        value={values.legalAddress2}
                        onChange={(e) => handleChange('legalAddress2')(e.target.value)}
                        disabled={!canEdit}
                        errorMsg={errors.legalAddress2 as string}
                      />
                    </div>
                  </div>

                  <div className='grid gap-1 sm:grid-cols-12 sm:items-center'>
                    <label className='text-sm font-medium text-gray-700 sm:col-span-2'>{t('settings:company.address.city')}</label>
                    <div className='sm:col-span-10'>
                      <Input value={values.city} onChange={(e) => handleChange('city')(e.target.value)} disabled={!canEdit} errorMsg={errors.city as string} />
                    </div>
                  </div>
                  <div className='grid gap-1 sm:grid-cols-12 sm:items-center'>
                    <label className='text-sm font-medium text-gray-700 sm:col-span-2'>{t('settings:company.address.state')}</label>
                    <div className='sm:col-span-10'>
                      <Input
                        value={values.state}
                        onChange={(e) => handleChange('state')(e.target.value)}
                        disabled={!canEdit}
                        errorMsg={errors.state as string}
                      />
                    </div>
                  </div>

                  <div className='grid gap-1 sm:grid-cols-12 sm:items-center'>
                    <label className='text-sm font-medium text-gray-700 sm:col-span-2'>{t('settings:company.address.zipcode')}</label>
                    <div className='sm:col-span-10'>
                      <Input value={values.zip} onChange={(e) => handleChange('zip')(e.target.value)} disabled={!canEdit} errorMsg={errors.zip as string} />
                    </div>
                  </div>
                  <div className='grid gap-1 sm:grid-cols-12 sm:items-center'>
                    <label className='text-sm font-medium text-gray-700 sm:col-span-2'>{t('settings:company.address.country')}</label>
                    <div className='sm:col-span-10'>
                      <Input
                        value={values.country}
                        onChange={(e) => handleChange('country')(e.target.value)}
                        disabled={!canEdit}
                        errorMsg={errors.country as string}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
      </Loadable>
    </PageLayout>
  );
};

export default CompanySettings;
